import { useState, useEffect } from "react";
import AdminNews from "./components/AdminNews";
import AdminActivities from "./components/AdminActivities";
import AdminAnnouncements from "./components/AdminAnnouncements";
import AdminSettings from "./components/AdminSettings";
import {
  clearWebAdminToken,
  getWebAdminToken,
  setWebAdminToken,
  webAdminLogin,
} from "@/lib/webContentApi";

type Tab = "news" | "activities" | "announcements" | "settings";

const tabs: { id: Tab; label: string; icon: string }[] = [
  { id: "news", label: "Noticias", icon: "ri-newspaper-line" },
  { id: "activities", label: "Actividades", icon: "ri-calendar-event-line" },
  { id: "announcements", label: "Comunicados", icon: "ri-notification-3-line" },
  { id: "settings", label: "Configuracion", icon: "ri-settings-3-line" },
];

export default function AdminPage() {
  const [activeTab, setActiveTab] = useState<Tab>("news");
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const token = getWebAdminToken();
    if (token) setAuthenticated(true);
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const result = await webAdminLogin(password);
      setWebAdminToken(result.token);
      setAuthenticated(true);
      setPassword("");
    } catch (loginError) {
      const message = loginError instanceof Error ? loginError.message : "No se pudo iniciar sesion";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    clearWebAdminToken();
    setAuthenticated(false);
    setPassword("");
    setError("");
  };

  const handleAuthExpired = () => {
    clearWebAdminToken();
    setAuthenticated(false);
    setError("Tu sesion expiro. Ingresa nuevamente.");
  };

  if (!authenticated) {
    return (
      <main className="min-h-screen bg-sky-50 flex items-center justify-center px-4 pt-20">
        <div className="w-full max-w-sm bg-white rounded-2xl p-8 border border-sky-100">
          <div className="flex flex-col items-center mb-8">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-sky-500 text-white mb-4">
              <i className="ri-shield-keyhole-line text-2xl"></i>
            </div>
            <h1 className="text-2xl font-extrabold text-sky-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Panel Administrativo
            </h1>
            <p className="text-slate-500 text-sm mt-1">APR Quechurehue</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Clave de acceso</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Ingresa la clave"
                required
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-sky-400 transition-colors"
              />
            </div>
            {error && (
              <div className="px-4 py-2.5 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
                <i className="ri-error-warning-line mr-2"></i>{error}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 rounded-xl bg-sky-500 hover:bg-sky-600 disabled:opacity-70 text-white font-bold transition-colors cursor-pointer whitespace-nowrap"
            >
              {submitting ? "Validando..." : "Ingresar"}
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50 pt-20">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-extrabold text-sky-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Panel Administrativo
            </h1>
            <p className="text-slate-500 text-sm">Gestiona el contenido del sitio web del APR</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors"
          >
            <i className="ri-logout-box-line"></i>
            Cerrar sesion
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-8 bg-white p-1.5 rounded-2xl border border-slate-100 w-fit">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all cursor-pointer ${
                activeTab === tab.id
                  ? "bg-sky-500 text-white"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <i className={tab.icon}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "news" && <AdminNews onAuthExpired={handleAuthExpired} />}
        {activeTab === "activities" && <AdminActivities onAuthExpired={handleAuthExpired} />}
        {activeTab === "announcements" && <AdminAnnouncements onAuthExpired={handleAuthExpired} />}
        {activeTab === "settings" && <AdminSettings onAuthExpired={handleAuthExpired} />}
      </div>
    </main>
  );
}

